import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CameraIcon,
  CloseIcon,
  DeleteOutlineIcon,
  ImageAdd,
} from 'assets/icons';
import { Alert, CircularLoading, Dialog, Image } from 'components/commons';
import ConfirmDeleteDialog from 'components/features/ConfirmDeleteDialog';
import SecuredImage from 'components/features/SecuredImage';
import {
  ConfirmAddUserDialog,
  ConfirmEditUserDialog,
  UserForm,
} from 'components/features/User';
import { API_PATHS } from 'constant/api';
import { PAGE_LIST } from 'constant/pages';
import i18next from 'i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Webcam from 'react-webcam';
import { getKeyFromLocalStorage, THRESHOLD_DEFAULT } from 'utils/constant';
import {
  createUser,
  deleteUser,
  getUserDetail,
  searchFaceId,
  updateUser,
} from 'utils/fetch';
import ja from 'utils/ja';

const StyleTypography = styled(Typography)(({ theme }) => ({
  fontSize: '22px',
  fontWeight: '700',
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
  },
}));

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const allowImgExt = ['jpeg', 'png', 'jpg'];

const UploadFileInput = styled('input')({
  display: 'none',
});

enum ImageTpe {
  AVATAR = 'avatar',
  FACEID = 'faceId',
}
export interface AddUserFormType {
  displayName: string;
  affiliation: string;
  tag: string;
  gender: string;
  age: number;
  group: string;
  idNumber: string;
  phoneNumber: string;
}

const ADD_USER_FORM_DEFAULT_VALUE = {
  displayName: '',
  affiliation: '',
  tag: '',
  gender: '',
  age: 0,
  group: '',
  idNumber: '',
  phoneNumber: '',
};

interface PreviewType {
  path: string;
  file: File;
}

interface AlertErrorType {
  isError: boolean;
  message: string;
  type: 'success' | 'info' | 'warning' | 'error';
}

const UserAdd = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isDetailScreen = !!id;

  const webcamPreviewRef = useRef<Webcam>(null);

  const [isEdit, setEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [openWebcam, setOpenWebcam] = useState<boolean>(false);
  const [webcamType, setWebcamType] = useState<ImageTpe>(ImageTpe.AVATAR);
  const [previewAvatar, setPreviewAvatar] = useState<PreviewType | null>(null);
  const [previewFaceIds, setPreviewFaceIds] = useState<PreviewType[]>([]);
  const [faceIds, setFaceIds] = useState<IFaceId[]>([]);
  const [deleteFaceIds, setDeleteFaceIds] = useState<IFaceId[]>([]);
  const [alertError, setAlertError] = useState<AlertErrorType>({
    isError: false,
    message: '',
    type: 'error',
  });
  const [dialogConfirmAdd, setDilogConfirmAdd] = useState<boolean>(false);
  const [dialogConfirmUpdate, setDilogConfirmUpdate] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const headerKey = {
    appId: getKeyFromLocalStorage('appId') ?? '',
    appKey: getKeyFromLocalStorage('appKey') ?? '',
    dbId: getKeyFromLocalStorage('dbId') ?? '',
    dbKey: getKeyFromLocalStorage('dbKey') ?? '',
  };

  const { control, handleSubmit, getValues, reset } = useForm<AddUserFormType>({
    defaultValues: ADD_USER_FORM_DEFAULT_VALUE,
  });
  const { displayName, tag } = getValues();

  const enableSaveBtn = useMemo(() => {
    return previewFaceIds?.length || faceIds?.length;
  }, [previewFaceIds, faceIds]);

  const handleGetUserDetail = async (id: string) => {
    try {
      const data: IFaceDetail | undefined = await getUserDetail(id, headerKey);

      if (data) {
        reset(data);
        setName(data?.displayName ?? '');
      }
      if (data?.faceIds?.length) setFaceIds(data.faceIds);
    } catch (error) {
      // empty
    }
  };

  function dataURLtoFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] ?? '';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  const validateFileImage = (file: File) => {
    if (file) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (file.size > MAX_FILE_SIZE) {
        return false;
      } else if (!allowImgExt.includes(fileExtension)) {
        return false;
      }
      return true;
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } },
    type: ImageTpe
  ) => {
    if (type === ImageTpe.AVATAR) {
      const files = event.target.files;
      if (files && files[0] && validateFileImage(files[0])) {
        const file = files[0];
        const fileUrl = URL.createObjectURL(file);

        setPreviewAvatar({ path: fileUrl, file: file });
      }
    }
    if (type === ImageTpe.FACEID) {
      const files = Array.from(event.target.files ?? []);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const validFiles: any[] = [];
      setLoading(true);
      Promise.allSettled(
        files.map((file) => {
          if (validateFileImage(file)) {
            return searchFaceId(headerKey, file, isDetailScreen, id).then(
              (result) => ({
                file,
                result,
              })
            );
          }
          return Promise.reject({ file, reason: 'Invalid file' });
        })
      )
        .then((values) => {
          let countFileError = 0;
          values.forEach((value) => {
            if (value.status === 'fulfilled' && value.value.result) {
              validFiles.push(value.value.file);
            } else {
              countFileError++;
            }
          });
          if (validFiles.length > 0) {
            setPreviewFaceIds([
              ...previewFaceIds,
              ...validFiles.map((file) => ({
                path: URL.createObjectURL(file),
                file: file,
              })),
            ]);

            if (!previewAvatar && !isDetailScreen) {
              setPreviewAvatar({
                path: URL.createObjectURL(validFiles[0]),
                file: validFiles[0],
              });
            }
          }
          if (countFileError) {
            setAlertError({
              isError: true,
              message: i18next.t(
                isDetailScreen
                  ? ja.message_error.file_upload_update
                  : ja.message_error.file_upload_add,
                {
                  0: countFileError,
                  1: values.length,
                }
              ),
              type: 'warning',
            });
          }
        })
        .catch(() => {
          setAlertError({
            isError: true,
            message: ja.message_error.internal_server,
            type: 'error',
          });
        })
        .finally(() => setLoading(false));
    }
  };

  const handleRemoveImage = (type: ImageTpe, index?: number) => {
    if (type === ImageTpe.AVATAR) {
      setPreviewAvatar(null);
    }

    if (type === ImageTpe.FACEID && index != null) {
      setPreviewFaceIds((oldState) =>
        oldState.filter((_item, iindex) => {
          return iindex != index;
        })
      );
    }
  };

  const capturePhoto = (type: ImageTpe) => {
    const imageSrc = webcamPreviewRef.current?.getScreenshot();
    if (imageSrc) {
      handleFileChange(
        {
          target: {
            files: [dataURLtoFile(imageSrc, 'captured_image.png')],
          },
        },
        type
      );
      setOpenWebcam(false);
    }
  };

  const handleDeleteFaceId = async (face: IFaceId) => {
    setDeleteFaceIds((oldState) => [...oldState, face]);
    setFaceIds((oldState) => oldState.filter((item) => item.id != face.id));
  };

  const onSubmit = async () => {
    if (isDetailScreen) {
      setDilogConfirmUpdate(true);
    } else {
      setDilogConfirmAdd(true);
    }
  };

  const handlePushData = async () => {
    const data = getValues();
    const formData = new FormData();
    const formAvartar = new FormData();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listFaceIdPush: any[] = [];

    if (previewAvatar) {
      formData.append('avatar', previewAvatar.file);
      formAvartar.append('avatar', previewAvatar.file);
    }

    if (previewFaceIds?.length) {
      previewFaceIds.map((file, index) => {
        listFaceIdPush.push({
          action: 'add',
          fieldname: `image${index}`,
          threshold: THRESHOLD_DEFAULT,
        });
        formData.append(`image${index}`, file.file);
      });
      if (!previewAvatar && !isDetailScreen) {
        formData.append('avatar', previewFaceIds[0].file);
        formAvartar.append('avatar', previewFaceIds[0].file);
      }
    }

    if (deleteFaceIds?.length) {
      deleteFaceIds.map((face) => {
        listFaceIdPush.push({
          action: 'remove',
          id: face.id,
        });
      });
    }

    const form = {
      ...data,
      gender: data.gender ? data.gender : id ? null : undefined,
      faceIds: listFaceIdPush,
    };

    formData.append('data', JSON.stringify(form));

    try {
      setLoading(true);
      if (isDetailScreen) {
        await updateUser(id, formData, headerKey, formAvartar);
      } else {
        await createUser(formData, headerKey);
      }
      navigate(PAGE_LIST.HOME.pathname());
    } catch (error) {
      setAlertError({
        isError: true,
        message: ja.message_error.internal_server,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) handleGetUserDetail(id);
  }, [id]);

  const handleConfirmDelete = async () => {
    try {
      await deleteUser(id ?? '', headerKey);
      navigate(PAGE_LIST.HOME.pathname());
    } catch (error) {
      //empty
    }
  };

  return (
    <Box
      component={'form'}
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        overflowY: 'auto',
        padding: '10px',
        height: { xs: 'calc(100dvh - 60px)', sm: 'calc(100dvh - 80px)' },
        mt: { xs: '60px', sm: '80px' },
      }}
    >
      <Divider sx={{ width: '100%', backgroundColor: '#BABABA', mb: '10px' }} />
      {!isDetailScreen ? (
        <StyleTypography>{ja.user_add.avatar_image}</StyleTypography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <StyleTypography>{ja.user_add.avatar_image}</StyleTypography>
          <Box sx={{ gap: '10px', display: 'flex' }}>
            {!isEdit && (
              <Button
                onClick={() => setEdit(!isEdit)}
                sx={{
                  padding: '8px 25px',
                  borderRadius: '6px',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '400',
                    color: theme.palette.secondary.light,
                  }}
                >
                  {ja.common.edit}
                </Typography>
              </Button>
            )}
            <Box
              onClick={() => setOpenDelete(true)}
              component='div'
              sx={{
                cursor: 'pointer',
                width: 50,
                height: 45,
                borderRadius: '4px',
                backgroundColor: theme.palette.error.main,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <DeleteOutlineIcon
                sx={{ fill: '#fff', fontSize: '40px', margin: 'auto' }}
              />
            </Box>
          </Box>
        </Box>
      )}
      {isDetailScreen ? (
        <>
          <Stack
            flexDirection='row'
            justifyContent={'flex-start'}
            gap='15px'
            sx={{ my: '10px' }}
          >
            <Box
              component='img'
              src={CameraIcon}
              alt=''
              sx={{
                width: { xs: 40, sm: 60 },
                height: { xs: 40, sm: 60 },
                cursor: isEdit ? 'pointer' : 'default',
                opacity: isEdit ? 1 : 0.6,
              }}
              onClick={() => {
                if (isEdit) {
                  setOpenWebcam(true);
                  setWebcamType(ImageTpe.AVATAR);
                }
              }}
            />
            <Box
              component='label'
              htmlFor={isEdit ? 'upload-avatar' : undefined}
            >
              <Box
                component='img'
                src={ImageAdd}
                alt=''
                sx={{
                  width: { xs: 40, sm: 60 },
                  height: { xs: 40, sm: 60 },
                  cursor: isEdit ? 'pointer' : 'default',
                  opacity: isEdit ? 1 : 0.6,
                }}
              />
            </Box>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '152px 1fr',
              gap: '10px',
              position: 'relative',
              mb: '10px',
            }}
          >
            <Box
              component='label'
              htmlFor='upload-avatar'
              width='152px'
              height='130px'
            >
              {previewAvatar ? (
                <Box
                  component='img'
                  src={previewAvatar.path}
                  alt=''
                  sx={{
                    width: 152,
                    height: 130,
                    objectFit: 'contain',
                    border: '1px solid #bababa',
                    cursor: isEdit ? 'pointer' : 'default',
                  }}
                />
              ) : (
                <SecuredImage
                  headerKey={headerKey}
                  src={`${API_PATHS.get_avatar_image(id)}`}
                  alt='face'
                  width='152px'
                  height='130px'
                  sx={{ cursor: isEdit ? 'pointer' : 'default' }}
                />
              )}
              <UploadFileInput
                id={isEdit ? 'upload-avatar' : undefined}
                type='file'
                onChange={(event) => handleFileChange(event, ImageTpe.AVATAR)}
              />
            </Box>
            <Box
              sx={{
                height: 'fit-content',
                overflowX: 'auto',
              }}
            >
              <Typography
                component='span'
                sx={{
                  mb: '20px',
                  fontSize: '26px',
                  fontWeight: '400',
                  position: 'absolute',
                  top: 0,
                  left: '182px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '1',
                  WebkitBoxOrient: 'vertical',
                  width: { xs: '32vw', sm: 'unset' },
                }}
              >
                {displayName}
              </Typography>
              <Stack flexDirection='row' gap='10px' mt='46px'>
                {tag &&
                  tag.split(',').map((item) => (
                    <Typography
                      component='span'
                      sx={{
                        padding: '4px 12px',
                        borderRadius: '16px',
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        fontSize: '16px',
                        fontWeight: '400',
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
              </Stack>
            </Box>
          </Box>
        </>
      ) : (
        <Box>
          <Stack
            flexDirection='row'
            justifyContent='center'
            gap='15px'
            sx={{ my: '10px' }}
          >
            {!previewAvatar && (
              <Box
                component='img'
                src={CameraIcon}
                alt=''
                sx={{
                  width: { xs: '60px', sm: '100px' },
                  height: { xs: '60px', sm: '100px' },
                }}
                onClick={() => {
                  setOpenWebcam(true);
                  setWebcamType(ImageTpe.AVATAR);
                }}
              />
            )}
            <Box position='relative'>
              <Box component='label' htmlFor='upload-avatar'>
                {previewAvatar ? (
                  <Box
                    component='img'
                    src={previewAvatar.path}
                    alt=''
                    sx={{
                      width: 152,
                      height: 130,
                      objectFit: 'contain',
                      border: '1px solid #bababa',
                    }}
                  />
                ) : (
                  <Box
                    component='img'
                    src={ImageAdd}
                    alt=''
                    sx={{
                      width: { xs: '60px', sm: '100px' },
                      height: { xs: '60px', sm: '100px' },
                    }}
                  />
                )}
                <UploadFileInput
                  id='upload-avatar'
                  type='file'
                  onChange={(event) => handleFileChange(event, ImageTpe.AVATAR)}
                />
              </Box>
              {previewAvatar && (
                <IconButton
                  sx={{
                    width: 37,
                    height: 37,
                    borderRadius: '2px',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: '#5F5F5FB2',
                    '&:hover': {
                      backgroundColor: '#5F5F5FB2',
                    },
                    zIndex: 100,
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRemoveImage(ImageTpe.AVATAR);
                  }}
                >
                  <CloseIcon sx={{ color: '#fff' }} />
                </IconButton>
              )}
            </Box>
          </Stack>
        </Box>
      )}
      <Divider sx={{ width: '100%', backgroundColor: '#BABABA' }} />
      <StyleTypography sx={{ mt: '10px' }}>
        {ja.user_add.property}
      </StyleTypography>
      <UserForm control={control} isEdit={!!id && !isEdit} />
      <Box>
        <Divider sx={{ width: '100%', backgroundColor: '#BABABA' }} />
        <StyleTypography sx={{ mt: '10px' }}>
          {ja.user_add.ref_photo}
          <Typography component='span' color={theme.palette.error.main}>
            *
          </Typography>
        </StyleTypography>
        <Stack
          flexDirection='row'
          justifyContent={
            !!faceIds.length || !!previewFaceIds.length
              ? 'flex-start'
              : 'center'
          }
          gap='15px'
          sx={{ my: '10px' }}
        >
          <Box
            component='img'
            src={CameraIcon}
            alt=''
            sx={{
              width:
                !!faceIds.length || !!previewFaceIds.length
                  ? { xs: '40px', sm: '60px' }
                  : { xs: '60px', sm: '100px' },
              height:
                !!faceIds.length || !!previewFaceIds.length
                  ? { xs: '40px', sm: '60px' }
                  : { xs: '60px', sm: '100px' },
              cursor: isDetailScreen
                ? isEdit
                  ? 'pointer'
                  : 'default'
                : 'pointer',
              opacity: isDetailScreen ? (isEdit ? 1 : 0.6) : 1,
            }}
            onClick={() => {
              if ((isDetailScreen && isEdit) || !isDetailScreen) {
                setOpenWebcam(true);
                setWebcamType(ImageTpe.FACEID);
              }
            }}
          />
          <Box component='label' htmlFor='upload-faceId'>
            <Box
              component='img'
              src={ImageAdd}
              alt=''
              sx={{
                width:
                  !!faceIds.length || !!previewFaceIds.length
                    ? { xs: '40px', sm: '60px' }
                    : { xs: '60px', sm: '100px' },
                height:
                  !!faceIds.length || !!previewFaceIds.length
                    ? { xs: '40px', sm: '60px' }
                    : { xs: '60px', sm: '100px' },
                cursor: isDetailScreen
                  ? isEdit
                    ? 'pointer'
                    : 'default'
                  : 'pointer',
                opacity: isDetailScreen ? (isEdit ? 1 : 0.6) : 1,
              }}
            />
            <UploadFileInput
              id={
                isDetailScreen
                  ? isEdit
                    ? 'upload-faceId'
                    : undefined
                  : 'upload-faceId'
              }
              type='file'
              multiple
              onChange={(event) => handleFileChange(event, ImageTpe.FACEID)}
            />
          </Box>
        </Stack>
        <Stack justifyContent='center' alignItems='center'>
          {(!!faceIds.length || !!previewFaceIds.length) && (
            <Stack
              flexDirection='row'
              justifyContent='flex-start'
              gap='10px'
              sx={{
                maxWidth: '90%',
                height: 138,
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              {faceIds.map((face, index) => (
                <Box key={`face-id-${index}`} sx={{ position: 'relative' }}>
                  <SecuredImage
                    headerKey={headerKey}
                    src={`${API_PATHS.get_face_image(id, face.id)}`}
                    alt='face'
                    width='152px'
                    height='130px'
                  />
                  <IconButton
                    sx={{
                      display: isDetailScreen
                        ? isEdit
                          ? 'inherit'
                          : 'none'
                        : 'inherit',
                      width: 37,
                      height: 37,
                      borderRadius: '2px',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      backgroundColor: '#5F5F5FB2',
                      '&:hover': {
                        backgroundColor: '#5F5F5FB2',
                      },
                      cursor: isDetailScreen
                        ? isEdit
                          ? 'pointer'
                          : 'default'
                        : 'pointer',
                    }}
                    onClick={() => {
                      if ((isDetailScreen && isEdit) || !isDetailScreen)
                        handleDeleteFaceId(face);
                    }}
                  >
                    <CloseIcon sx={{ color: '#fff' }} />
                  </IconButton>
                </Box>
              ))}
              {previewFaceIds.map((file, index) => (
                <Image
                  key={`face-id-preview-${index}`}
                  imageUrl={file.path ?? ''}
                  onDelete={() => {
                    if ((isDetailScreen && isEdit) || !isDetailScreen) {
                      handleRemoveImage(ImageTpe.FACEID, index);
                    }
                  }}
                  alt=''
                  width={152}
                  height={130}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Box>
      <Box sx={{ textAlign: 'center', mt: '10px' }}>
        <Button
          type='submit'
          disabled={isDetailScreen ? !enableSaveBtn || !isEdit : !enableSaveBtn}
          sx={{
            padding: '13px 31px',
            borderRadius: '4px',
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
            '&:disabled': {
              backgroundColor: '#ccc',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: '400',
              color: theme.palette.secondary.light,
            }}
          >
            {ja.common.save}
          </Typography>
        </Button>
      </Box>
      {openWebcam && (
        <Dialog
          open={true}
          handleClose={() => setOpenWebcam(false)}
          title={ja.user_add.photo_register}
          handleConfirm={() => capturePhoto(webcamType)}
        >
          <Grid>
            <Webcam
              key='camera'
              mirrored={true}
              audio={false}
              width={480}
              height={640}
              ref={webcamPreviewRef}
              screenshotFormat='image/jpeg'
              videoConstraints={{
                width: 480,
                height: 640,
                facingMode: 'user',
              }}
              style={{ width: '100%', borderRadius: 8 }}
            />
          </Grid>
        </Dialog>
      )}
      <CircularLoading loading={loading} />
      <Alert
        open={alertError.isError}
        onClose={() =>
          setAlertError({ isError: false, message: '', type: 'error' })
        }
        message={alertError.message}
        type={alertError.type}
      />
      <ConfirmAddUserDialog
        open={dialogConfirmAdd}
        onClose={() => setDilogConfirmAdd(false)}
        handleConfirm={() => {
          setDilogConfirmAdd(false);
          handlePushData();
        }}
      />
      <ConfirmEditUserDialog
        open={dialogConfirmUpdate}
        onClose={() => setDilogConfirmUpdate(false)}
        handleConfirm={() => {
          setDilogConfirmUpdate(false);
          handlePushData();
        }}
      />
      {isDetailScreen && (
        <ConfirmDeleteDialog
          open={openDelete}
          handleClose={() => setOpenDelete(false)}
          confirmTitle={ja.home.dialog.title_delete}
          confirmContent={i18next.t(ja.home.dialog.content_delete, {
            0: name,
          })}
          handleConfirm={handleConfirmDelete}
        />
      )}
    </Box>
  );
};

export default UserAdd;
